import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import { FC, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { RequestByIdApiType, useEditRequestAuto } from '@/api/models/requests'
import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import { ServerValidationError } from '@/api/shared/types'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { PassAutosFormBlock } from '@/components/entities/passes/ui/PassForms/FormBlock/PassAutosFormBlock'
import { useParkingPlaces } from '@/components/entities/requests/hooks/useParkingPlaces'
import {
  AutosFormData,
  AutosFormErrors,
  generateAutoPassFormSchema
} from '@/components/entities/requests/ui/model/schemas'
import { getDefaultAutos } from '@/components/entities/requests/ui/model/utils'
import { DrawerFormLayout } from '@/components/shared/drawer-form-layout'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { isServerValidationError } from '@/utils/parseServerErrorField'

type Props = {
  request?: RequestByIdApiType
  editingAutoId?: string
  onSuccess: () => void
  onError: (error: AxiosError<ServerValidationError>) => void
}

export const RequestEditingForm: FC<Props> = ({
  request,
  editingAutoId,
  onSuccess,
  onError
}) => {
  const { parkingPlaces } = useParkingPlaces()
  const { reachGoal } = useYandexMetrika()

  const {
    mutate: editAuto,
    isPending,
    error: editError
  } = useEditRequestAuto(request?.id ?? '')

  const validationError = isServerValidationError(editError) ? editError : null

  const editingAuto = useMemo(
    () =>
      request?.autos?.find(({ id }) => id === editingAutoId) ??
      request?.autos?.[0],
    [request?.autos, editingAutoId]
  )

  const editingRequest = useMemo(
    () =>
      request && editingAuto ? { ...request, autos: [editingAuto] } : request,
    [request, editingAuto]
  )

  const form = useForm<AutosFormData, AutosFormErrors>({
    defaultValues: {
      room: undefined,
      autos: getDefaultAutos(editingRequest, parkingPlaces)
    },
    mode: 'onSubmit',
    resolver: yupResolver(generateAutoPassFormSchema())
  })

  const { handleSubmit, reset, setError } = form

  const isPropertiesPass =
    request?.typeAlias === RequestTypeAlias.PropertiesPass

  useEffect(() => {
    reset({
      room: undefined,
      autos: getDefaultAutos(editingRequest, parkingPlaces)
    })
  }, [parkingPlaces, editingRequest, reset])

  useEffect(() => {
    const serverMessage =
      validationError?.response?.data?.errors?.[0]?.message ||
      validationError?.response?.data?.title

    if (serverMessage) {
      setError('autos.0.number', { type: 'server', message: serverMessage })
    }
  }, [validationError, setError])

  const handleFormSubmit = (data: AutosFormData) => {
    reachGoal(YandexMetrikaEvent.PASSES_EDIT_AUTO_END)

    const auto = data.autos?.[0]
    const autoId = editingAuto?.id

    if (!auto || !autoId) {
      return
    }

    editAuto(
      {
        autoId,
        body: {
          number: auto.number ?? '',
          model: auto.model ?? '',
          isStandard: Boolean(auto.isStandard)
        }
      },
      {
        onSuccess,
        onError: error => {
          if (isServerValidationError(error)) {
            return
          }

          onError(error)
        }
      }
    )
  }

  return (
    <FormProvider {...form}>
      <DrawerFormLayout
        onSubmit={handleSubmit(handleFormSubmit)}
        isPending={isPending}
        submitLabel="Сохранить"
      >
        <PassAutosFormBlock
          typeAlias={
            isPropertiesPass
              ? RequestTypeAlias.PropertiesPass
              : RequestTypeAlias.RentPassAuto
          }
          singleCarMode
        />
      </DrawerFormLayout>
    </FormProvider>
  )
}
